import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Link, useLocation } from 'react-router-dom'
import type { EmailTask, TaskStatus } from '../types'

type ViewMode = 'list' | 'gantt'

function statusLabel(status: TaskStatus): string {
  if (status === 'wrong_redo') return 'Wrong / Redo'
  return status
}

function dayMs(iso: string | null | undefined): number | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

function addDays(ms: number, n: number): number {
  return ms + n * 86_400_000
}

function shortWeekday(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    weekday: 'narrow',
    timeZone: 'UTC',
  })
}

function monthLabel(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short',
    timeZone: 'UTC',
  })
}

function dayNum(ms: number): string {
  return String(new Date(ms).getUTCDate())
}

function formatUtcSpan(start: number, end: number): string {
  const a = new Date(start).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
  const b = new Date(end).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
  return `${a} → ${b}`
}

type ChartRange = { min: number; max: number; days: number; today: number }

function computeRange(tasks: EmailTask[], parent: EmailTask): ChartRange {
  const today = dayMs(new Date().toISOString())!
  const points: number[] = [today, addDays(today, 4), addDays(today, -2)]
  for (const t of [parent, ...tasks]) {
    const c = dayMs(t.created_at)
    const d = dayMs(t.due_at)
    if (c != null) points.push(c)
    if (d != null) points.push(d)
  }
  let min = Math.min(...points)
  let max = Math.max(...points)
  if (max <= min) max = addDays(min, 7)
  min = addDays(min, -1)
  max = addDays(max, 1)
  const days = Math.max(1, Math.round((max - min) / 86_400_000) + 1)
  return { min, max, days, today }
}

function barGeom(
  t: EmailTask,
  range: ChartRange,
): { colStart: number; colSpan: number; hasDue: boolean } {
  const created = dayMs(t.created_at) ?? range.min
  const due = dayMs(t.due_at)
  const hasDue = due != null
  const start = Math.max(range.min, created)
  const end = Math.min(range.max, hasDue ? Math.max(due, created) : created)
  const colStart = Math.max(0, Math.round((start - range.min) / 86_400_000))
  const colEnd = Math.max(colStart, Math.round((end - range.min) / 86_400_000))
  const colSpan = Math.max(1, colEnd - colStart + 1)
  return { colStart, colSpan: Math.min(range.days - colStart, colSpan), hasDue }
}

type GanttProps = {
  tasks: EmailTask[]
  parent: EmailTask
  linkState?: { listSearch?: string }
}

function SubtasksGantt({ tasks, parent, linkState }: GanttProps) {
  const range = useMemo(() => computeRange(tasks, parent), [tasks, parent])

  const dayCols = useMemo(() => {
    const cols: {
      ms: number
      month: string
      day: string
      isToday: boolean
      isWeekend: boolean
      showMonth: boolean
    }[] = []
    let lastMonth = ''
    for (let i = 0; i < range.days; i += 1) {
      const ms = addDays(range.min, i)
      const month = monthLabel(ms)
      const dow = new Date(ms).getUTCDay()
      cols.push({
        ms,
        month,
        day: dayNum(ms),
        isToday: ms === range.today,
        isWeekend: dow === 0 || dow === 6,
        showMonth: month !== lastMonth,
      })
      lastMonth = month
    }
    return cols
  }, [range])

  const todayIdx =
    range.today >= range.min && range.today <= range.max
      ? Math.round((range.today - range.min) / 86_400_000)
      : null

  const rows: { task: EmailTask; kind: 'parent' | 'child' }[] = [
    { task: parent, kind: 'parent' },
    ...tasks.map((task) => ({ task, kind: 'child' as const })),
  ]

  return (
    <div className="gantt" role="img" aria-label="Sub-todo Gantt chart">
      <div className="gantt-scroll">
        <div
          className="gantt-grid"
          style={{ '--gantt-days': range.days } as CSSProperties}
        >
          <div className="gantt-corner" aria-hidden />
          <div className="gantt-head">
            {dayCols.map((c) => (
              <div
                key={c.ms}
                className={`gantt-col-head${c.isToday ? ' is-today' : ''}${
                  c.isWeekend ? ' is-weekend' : ''
                }`}
                title={`${c.month} ${c.day}`}
              >
                {c.showMonth ? <span className="gantt-moy">{c.month}</span> : null}
                <span className="gantt-dow">{shortWeekday(c.ms)}</span>
                <span className="gantt-dom">{c.day}</span>
              </div>
            ))}
          </div>

          {rows.map(({ task: t, kind }) => {
            const geom = barGeom(t, range)
            const created = dayMs(t.created_at) ?? range.min
            const due = dayMs(t.due_at) ?? created
            return (
              <div key={t.id} className={`gantt-body-row kind-${kind}`}>
                <div className="gantt-name">
                  {kind === 'parent' ? (
                    <span className="gantt-name-text" title={t.title}>
                      {t.title}
                      <em>parent</em>
                    </span>
                  ) : (
                    <Link
                      to={`/tasks/${t.id}`}
                      state={linkState}
                      className="gantt-name-text"
                      title={t.title}
                    >
                      {t.title}
                    </Link>
                  )}
                </div>
                <div className="gantt-lanes">
                  {dayCols.map((c) => (
                    <div
                      key={c.ms}
                      className={`gantt-cell${c.isWeekend ? ' is-weekend' : ''}${
                        c.isToday ? ' is-today' : ''
                      }`}
                    />
                  ))}
                  {todayIdx != null ? (
                    <span
                      className="gantt-today-line"
                      style={{
                        left: `calc((100% / ${range.days}) * ${todayIdx} + (100% / ${range.days}) / 2)`,
                      }}
                    />
                  ) : null}
                  <span
                    className={`gantt-bar status-${t.status}${geom.hasDue ? '' : ' is-undated'}${
                      kind === 'parent' ? ' is-parent' : ''
                    }`}
                    style={{
                      left: `calc((100% / ${range.days}) * ${geom.colStart} + 2px)`,
                      width: `calc((100% / ${range.days}) * ${geom.colSpan} - 4px)`,
                    }}
                    title={
                      geom.hasDue
                        ? formatUtcSpan(created, due)
                        : `Created ${formatUtcSpan(created, created).split(' → ')[0]} (no due)`
                    }
                  >
                    <span className="gantt-bar-label">
                      {geom.hasDue ? dayNum(due) : '·'}
                    </span>
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <p className="gantt-hint">
        Bars span created → due · today is the red line · weekends shaded
      </p>
    </div>
  )
}

type Props = {
  parent: EmailTask
  childrenTasks: EmailTask[]
}

export function SubtasksSection({ parent, childrenTasks }: Props) {
  const location = useLocation()
  const fromState = (location.state as { listSearch?: string } | null)?.listSearch
  const listSearch =
    typeof fromState === 'string'
      ? fromState
      : (sessionStorage.getItem('tasks.listSearch') ?? undefined)
  const linkState = listSearch != null ? { listSearch } : undefined
  const [view, setView] = useState<ViewMode>('list')
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [menuOpen])

  if (!childrenTasks.length) return null

  return (
    <section className="subtasks">
      <div className="subtasks-head">
        <h2>Sub-todos</h2>
        <div className="subtasks-view-menu" ref={menuRef}>
          <button
            type="button"
            className="view-eye"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Sub-todo view"
            title="Change view"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
                stroke="currentColor"
                strokeWidth="1.6"
              />
              <circle cx="12" cy="12" r="2.6" fill="currentColor" />
            </svg>
          </button>
          {menuOpen ? (
            <ul className="overflow-menu view-menu" role="menu">
              <li role="none">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={view === 'list'}
                  className={view === 'list' ? 'is-active' : undefined}
                  onClick={() => {
                    setView('list')
                    setMenuOpen(false)
                  }}
                >
                  List
                </button>
              </li>
              <li role="none">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={view === 'gantt'}
                  className={view === 'gantt' ? 'is-active' : undefined}
                  onClick={() => {
                    setView('gantt')
                    setMenuOpen(false)
                  }}
                >
                  Gantt
                </button>
              </li>
            </ul>
          ) : null}
        </div>
      </div>

      {view === 'list' ? (
        <ul>
          {childrenTasks.map((c) => (
            <li key={c.id}>
              <Link to={`/tasks/${c.id}`} state={linkState}>
                {c.title}
              </Link>
              <span className={`status status-${c.status}`}>{statusLabel(c.status)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <SubtasksGantt tasks={childrenTasks} parent={parent} linkState={linkState} />
      )}
    </section>
  )
}
